import { TrendingUp, Users, Eye, CheckCircle, Instagram, Twitter } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';

const topInfluencers = [
  {
    id: 1,
    name: 'Sarah Mitchell',
    handle: '@AdventureWithSarah',
    avatar: 'https://images.unsplash.com/photo-1768347441028-032fd139bd56?w=200',
    platform: 'instagram',
    followers: 487000,
    engagement: 8.4,
    mentions: 47,
    category: 'Adventure Travel',
    verified: true,
    recentPost: 'Day 3 of the Appalachian Trail and my Moab 3s are holding up PERFECTLY!',
    reach: 2840000
  },
  {
    id: 2,
    name: 'Jake Thompson',
    handle: '@TrailRunnerJake',
    avatar: 'https://images.unsplash.com/photo-1762709753300-342ab94e8b05?w=200',
    platform: 'twitter',
    followers: 324000,
    engagement: 6.2,
    mentions: 38,
    category: 'Trail Running',
    verified: true,
    recentPost: 'Just PR\'d my favorite trail run in the Agility Peak 5!',
    reach: 1950000
  },
  {
    id: 3,
    name: 'Mike Anderson',
    handle: '@OutdoorLifeMike',
    avatar: 'https://images.unsplash.com/photo-1663550369129-b25db8cacaf2?w=200',
    platform: 'youtube',
    followers: 892000,
    engagement: 9.1,
    mentions: 52,
    category: 'Gear Reviews',
    verified: true,
    recentPost: 'Tested 15 different hiking boots this season. The Moab 3 consistently outperformed...',
    reach: 4560000
  },
  {
    id: 4,
    name: 'Beth Rodriguez',
    handle: '@BackpackingBeth',
    avatar: 'https://images.unsplash.com/photo-1548256921-d8a1f19bd52c?w=200',
    platform: 'youtube',
    followers: 567000,
    engagement: 7.8,
    mentions: 34,
    category: 'Backpacking',
    verified: true,
    recentPost: 'NEW VIDEO: 7-day backcountry trip gear review! My Merrell boots were the MVP.',
    reach: 3210000
  },
  {
    id: 5,
    name: 'Daniel Chen',
    handle: '@NaturePhotoDan',
    avatar: 'https://images.unsplash.com/photo-1682695795798-1b31ea040caf?w=200',
    platform: 'instagram',
    followers: 623000,
    engagement: 8.9,
    mentions: 29,
    category: 'Outdoor Photography',
    verified: true,
    recentPost: 'Chasing waterfalls in the PNW. These Moab Speed 2s are perfect...',
    reach: 2890000
  },
  {
    id: 6,
    name: 'Emily Torres',
    handle: '@UltraRunnerEmily',
    avatar: 'https://images.unsplash.com/photo-1768347441028-032fd139bd56?w=200',
    platform: 'twitter',
    followers: 276000,
    engagement: 7.3,
    mentions: 31,
    category: 'Ultra Running',
    verified: true,
    recentPost: 'Mile 47 of 100. Still no hot spots. The Agility Peak 5 is the real deal...',
    reach: 1650000
  }
];

const formatNumber = (num: number) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(0) + 'K';
  }
  return num.toString();
};

const getPlatformIcon = (platform: string) => {
  switch (platform) {
    case 'instagram':
      return <Instagram className="w-4 h-4" style={{ color: '#e4405f' }} />;
    case 'twitter':
      return <Twitter className="w-4 h-4" style={{ color: '#1da1f2' }} />;
    default:
      return null;
  }
};

export function BrandPulseTopInfluencers() {
  return (
    <div className="grid grid-cols-2 gap-6">
      {topInfluencers.map((influencer) => (
        <Card key={influencer.id} className="border-0 shadow-sm hover:shadow-md transition-all">
          <CardContent className="p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-200 flex-shrink-0 ring-2 ring-purple-100">
                <ImageWithFallback
                  src={influencer.avatar}
                  alt={influencer.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-gray-900 font-medium">{influencer.name}</h3>
                      {influencer.verified && (
                        <CheckCircle className="w-4 h-4 text-blue-500 fill-blue-500" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-gray-600">{influencer.handle}</span>
                      {getPlatformIcon(influencer.platform)}
                    </div>
                    <Badge className="bg-purple-50 text-purple-700 border border-purple-200 text-xs">
                      {influencer.category}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
                  <Users className="w-3 h-3" />
                  <span>Followers</span>
                </div>
                <div className="text-gray-900 font-medium">{formatNumber(influencer.followers)}</div>
              </div>
              <div>
                <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
                  <TrendingUp className="w-3 h-3" />
                  <span>Engagement</span>
                </div>
                <div className="text-gray-900 font-medium">{influencer.engagement}%</div>
              </div>
              <div>
                <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
                  <Eye className="w-3 h-3" />
                  <span>Reach</span>
                </div>
                <div className="text-gray-900 font-medium">{formatNumber(influencer.reach)}</div>
              </div>
            </div>

            <div className="mb-4">
              <div className="text-xs text-gray-500 mb-1">Recent Mention</div>
              <p className="text-sm text-gray-700 line-clamp-2">{influencer.recentPost}</p>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-medium text-purple-600">{influencer.mentions}</span> mentions this month
              </div>
              <Button className="bg-purple-600 text-white hover:bg-purple-700 text-sm h-9">
                View Profile
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
